/**
 * platform-ai-settings-page.tsx — Platform → AI → Cài đặt token (`/platform/ai/credit-settings`).
 * Bật/tắt AI của eCDP toàn hệ thống, tỷ giá USD, BIÊN GIÁ BÁN RA (giá bán ra = giá mua vào × (1 + biên %); 0% = bán đúng giá
 * mua vào), mức trừ khi nhúng vượt trần, gói mua thêm (giá + giá trị nạp đ, hiện ≈ token theo MODEL THAM CHIẾU chọn được).
 * "Đơn vị tính nội bộ" (creditValueVnd) chỉ đọc — ví lưu theo đơn vị này, khách chỉ thấy đ / ≈ token.
 * Quyền lợi AI theo gói (token tặng / tháng, trần nhúng, trần ngày) sửa ở Bảng giá gói.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { Loader2, Plus, Save, SlidersHorizontal, Trash2 } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { EmptyState, ErrorState, Loading } from '@/components/shared/feedback'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/misc'
import { aiErrorMessage, formatDong, formatTokens, salePriceLine } from '@/lib/ai-credits'
import {
  usePlatformAiCatalog, usePlatformAiSettings, useSavePlatformAiSettings, type PlatformAiSettings,
} from '@/hooks/use-platform-ai'
import {
  newPackId, packValueOf, referenceCandidates, referenceModelOf, salePriceFor, settingsError, unitsForValue, unitsToTokensRef,
} from './platform-ai-lib'

function NumField({ label, value, onChange, suffix, hint, step, readOnly }: {
  label: string; value: number; onChange?: (v: number) => void; suffix?: string; hint?: string; step?: string; readOnly?: boolean
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="flex items-center gap-2">
        <Input
          type="number" min={0} step={step ?? '1'} value={Number.isFinite(value) ? value : ''} readOnly={readOnly} disabled={readOnly}
          onChange={(e) => onChange?.(e.target.value === '' ? Number.NaN : Number(e.target.value))}
        />
        {suffix && <span className="shrink-0 text-sm text-muted-foreground">{suffix}</span>}
      </div>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}

export function PlatformAiSettingsPage() {
  const { data, isLoading, isError } = usePlatformAiSettings()
  const save = useSavePlatformAiSettings()
  const catalog = usePlatformAiCatalog()
  const [draft, setDraft] = useState<PlatformAiSettings | null>(null)
  const [refKey, setRefKey] = useState<string | null>(null)
  useEffect(() => { if (data) setDraft(structuredClone(data)) }, [data])
  const candidates = useMemo(() => referenceCandidates(catalog.data), [catalog.data])
  const ref = useMemo(() => referenceModelOf(catalog.data, refKey), [catalog.data, refKey])

  if (isLoading) return <Loading />
  if (isError) return <ErrorState />
  if (data === null) {
    return (
      <div className="space-y-4">
        <PageHeader title="Cài đặt token AI" />
        <EmptyState icon={SlidersHorizontal} title="Máy chủ chưa hỗ trợ cài đặt token" description="Tính năng AI hai chế độ đang được triển khai phía máy chủ." />
      </div>
    )
  }
  if (!draft) return <Loading />

  const set = (p: Partial<PlatformAiSettings>) => setDraft((d) => (d ? { ...d, ...p } : d))
  const setPack = (i: number, p: Partial<PlatformAiSettings['packs'][number]>) =>
    setDraft((d) => (d ? { ...d, packs: d.packs.map((x, j) => (j === i ? { ...x, ...p } : x)) } : d))
  // Xem trước theo cài đặt ĐANG SỬA (chưa lưu) — cùng công thức server.
  const refSale = ref ? salePriceFor(ref.priceIn, ref.priceOut, draft) : null
  const tokensOf = (units: number) => unitsToTokensRef(units, draft, ref)

  const onSave = () => {
    const err = settingsError(draft)
    if (err) return void toast.error(err)
    save.mutate(draft, { onSuccess: () => toast.success('Đã lưu cài đặt token AI'), onError: (e) => toast.error(aiErrorMessage(e)) })
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Cài đặt token AI"
        description="Tỷ giá, biên giá bán ra và gói mua thêm. Giá bán ra / 1 triệu token và số ≈ token ở mọi màn cập nhật theo."
        actions={<Button onClick={onSave} disabled={save.isPending}>{save.isPending ? <Loader2 className="animate-spin" /> : <Save className="h-4 w-4" />} Lưu</Button>}
      />

      <Card>
        <CardHeader>
          <CardTitle>AI của eCDP</CardTitle>
          <CardDescription>Tắt = mọi tổ chức dùng nguồn “AI của eCDP” tạm dừng (Key của tôi vẫn chạy).</CardDescription>
        </CardHeader>
        <CardContent>
          <label className="flex items-center gap-3 text-sm">
            <Switch checked={draft.platformEnabled} onCheckedChange={(v) => set({ platformEnabled: v })} aria-label="Bật AI của eCDP" />
            {draft.platformEnabled ? 'Đang bật cho toàn hệ thống' : 'Đang tắt cho toàn hệ thống'}
          </label>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Giá bán ra</CardTitle>
          <CardDescription>
            Giá bán ra = giá mua vào (USD / 1 triệu token của nhà cung cấp) × tỷ giá × (1 + biên %). Mỗi lượt trừ ví đúng token thật ×
            giá bán ra của model; khách thấy số dư “≈ N token” theo model tổ chức đang dùng.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <NumField
              label="Biên giá bán ra (%)" suffix="%" value={draft.markupPercent} onChange={(v) => set({ markupPercent: v })}
              hint="0% = bán đúng giá mua vào."
            />
            <NumField label="Tỷ giá USD" suffix="đ" value={draft.usdVnd} onChange={(v) => set({ usdVnd: v })} />
            <NumField
              label="Giá trị 1 lượt AI" suffix="đ" value={draft.turnValueVnd ?? 10} onChange={(v) => set({ turnValueVnd: v })}
              hint="Khách thấy mọi số dư / gói / lịch sử theo lượt AI. 10đ ≈ 1 câu trả lời Gemini 3.1 Flash-Lite ở biên 0%."
            />
            <NumField
              label="Nhúng vượt trần" suffix="đ / 1.000 lần" step="10"
              value={Math.round(draft.embeddingOverageCreditsPer1000 * draft.creditValueVnd * 100) / 100}
              onChange={(v) => set({ embeddingOverageCreditsPer1000: draft.creditValueVnd > 0 ? v / draft.creditValueVnd : Number.NaN })}
            />
            <NumField
              label="Đơn vị tính nội bộ (đ)" suffix="đ" value={draft.creditValueVnd} readOnly
              hint="Chỉ đọc — ví lưu theo đơn vị này, khách không thấy."
            />
          </div>
          <div className="space-y-1.5 rounded-lg border bg-muted/30 p-3 text-sm">
            <label className="flex flex-wrap items-center gap-2">
              <span className="text-muted-foreground">Model tham chiếu (quy ≈ token ở gói mua thêm, bảng giá gói, AI theo tổ chức):</span>
              <select
                className="h-8 rounded-md border bg-transparent px-2 text-sm dark:bg-card"
                value={ref ? `${ref.provider}:${ref.model}` : ''}
                onChange={(e) => setRefKey(e.target.value || null)}
                aria-label="Model tham chiếu"
                disabled={!candidates.length}
              >
                {!candidates.length && <option value="">— Chưa có model đang bán —</option>}
                {candidates.map((m) => (
                  <option key={`${m.provider}:${m.model}`} value={`${m.provider}:${m.model}`}>
                    {m.title}{m.isReference ? ' (mặc định)' : ''}
                  </option>
                ))}
              </select>
            </label>
            {ref && refSale && (
              <p>
                {ref.title} (mua vào {ref.priceIn} $ / {ref.priceOut} $ mỗi 1 triệu token) → giá bán ra <b>{salePriceLine(refSale)}</b>.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Gói mua thêm</CardTitle>
          <CardDescription>
            Giá chưa VAT — VAT cộng khi thanh toán theo thuế suất ở Bảng giá gói. Giá trị nạp &gt; giá = gói tặng thêm. Khách thấy
            “giá ≈ N token” theo model tổ chức của họ; cột ≈ token ở đây quy theo model tham chiếu{ref ? ` (${ref.title})` : ''}.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full min-w-[640px] text-sm">
              <thead className="bg-muted">
                <tr className="border-b">
                  <th className="px-3 py-2 text-left font-medium">Mã</th>
                  <th className="px-3 py-2 text-right font-medium">Giá (chưa VAT)</th>
                  <th className="px-3 py-2 text-right font-medium">Giá trị nạp (đ)</th>
                  <th className="px-3 py-2 text-right font-medium">≈ token{ref ? ` ${ref.title}` : ''}</th>
                  <th className="px-3 py-2 text-left font-medium">Nhãn</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {draft.packs.map((p, i) => (
                  <tr key={i} className="border-b last:border-b-0">
                    <td className="px-3 py-2"><Input className="h-8 w-32 font-mono" value={p.id} onChange={(e) => setPack(i, { id: e.target.value })} aria-label="Mã gói" /></td>
                    <td className="px-3 py-2"><Input className="ml-auto h-8 w-36 text-right" type="number" min={0} step={1000} value={p.price} onChange={(e) => setPack(i, { price: Number(e.target.value) })} aria-label="Giá" /></td>
                    <td className="px-3 py-2">
                      <Input
                        className="ml-auto h-8 w-36 text-right" type="number" min={0} step={1000} value={packValueOf(p, draft)} aria-label="Giá trị nạp"
                        onChange={(e) => setPack(i, { credits: unitsForValue(Number(e.target.value), draft) })}
                      />
                      {p.price > 0 && packValueOf(p, draft) > p.price && (
                        <p className="mt-0.5 text-right text-xs text-success">Tặng thêm {Math.round((packValueOf(p, draft) / p.price - 1) * 100)}%</p>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {tokensOf(p.credits) != null ? `≈ ${formatTokens(tokensOf(p.credits))}` : formatDong(packValueOf(p, draft))}
                    </td>
                    <td className="px-3 py-2"><Input className="h-8 w-40" value={p.label ?? ''} placeholder="vd: Tặng thêm 10%" onChange={(e) => setPack(i, { label: e.target.value || null })} aria-label="Nhãn" /></td>
                    <td className="px-3 py-2 text-right">
                      <Button variant="ghost" size="icon" title="Xoá gói" onClick={() => set({ packs: draft.packs.filter((_, j) => j !== i) })}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button variant="outline" size="sm" onClick={() => set({ packs: [...draft.packs, { id: newPackId(draft.packs), credits: unitsForValue(100_000, draft) || 1, price: 100_000, label: null }] })}>
            <Plus className="h-4 w-4" /> Thêm gói
          </Button>
        </CardContent>
      </Card>

      <p className="text-sm text-muted-foreground">
        Token AI tặng / tháng, trần nhúng miễn phí / ngày và trần token AI / ngày của từng gói dịch vụ sửa ở{' '}
        <Link to="/platform/pricing" className="text-primary hover:underline">Bảng giá gói</Link>.
      </p>
    </div>
  )
}
